import * as React from "react";
import { cn } from "../_internal/cn";
import type { TypeNarrativeHeadlineRollProps } from "./NarrativeHeadlineTypes";

/**
 * NarrativeHeadlineRoll — the "roll" variant: a vertical word roll for the
 * highlighted slot of a headline. The active word slides into view while the
 * previous one rolls out (no typing). Inspired by the performative-ui WordRoll.
 *
 *     <NarrativeHeadline>
 *       Built for{" "}
 *       <NarrativeHeadlineRoll words={["marketers", "agencies", "teams"]} />
 *     </NarrativeHeadline>
 *
 * The container width follows the active word so it sits naturally inline.
 * Gradient-painted by default; honors `prefers-reduced-motion` by holding the
 * first word static.
 */
const NarrativeHeadlineRoll = React.forwardRef<
  HTMLSpanElement,
  TypeNarrativeHeadlineRollProps
>(
  (
    {
      words,
      intervalMs = 2200,
      transitionMs = 500,
      direction = "up",
      gradient = true,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    const [i, setI] = React.useState(0);

    React.useEffect(() => {
      if (words.length <= 1) return;

      // Respect reduced-motion: hold the first word, skip the cycle entirely.
      const reduced =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) return;

      const t = setInterval(
        () => setI((j) => (j + 1) % words.length),
        intervalMs
      );
      return () => clearInterval(t);
    }, [intervalMs, words.length]);

    const merged: React.CSSProperties = {
      ...style,
      // Consumed by the transition timing in narrative-headline.css.
      ["--seeds-narrative-headline-roll-ms" as string]: `${transitionMs}ms`,
    };

    const prev = words.length ? (i - 1 + words.length) % words.length : 0;

    return (
      <span
        ref={ref}
        className={cn(
          "seeds-narrative-headline-roll",
          { "seeds-narrative-headline-roll-down": direction === "down" },
          { "seeds-narrative-headline-roll-gradient": gradient },
          className
        )}
        style={merged}
        {...rest}
      >
        {/* Sizer keeps the container width matched to the active word — without
            it the absolutely-positioned words would collapse the box. */}
        <span
          className="seeds-narrative-headline-roll-sizer"
          aria-hidden="true"
        >
          {words[i]}
        </span>
        {words.map((w, idx) => (
          <span
            key={idx}
            className={cn("seeds-narrative-headline-roll-word", {
              "seeds-narrative-headline-roll-word-active": idx === i,
              "seeds-narrative-headline-roll-word-past":
                idx === prev && i !== prev,
            })}
            aria-hidden={idx === i ? undefined : "true"}
          >
            {w}
          </span>
        ))}
      </span>
    );
  }
);

NarrativeHeadlineRoll.displayName = "NarrativeHeadlineRoll";

export default NarrativeHeadlineRoll;
